import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { AddCircleOutline, CloseRounded } from '@mui/icons-material'
import { forwardRef, useState } from 'react'
import { useGetOrderRequests, TitleOrderRequest } from '../../logic/useApiQuery'
import { useCancelOrderRequest } from '../../logic/useApiMutation'
import { OrderRequestForm } from './OrderRequestForm'
import { useGetConfig } from '../../logic/useApiUnauthedQuery'

const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning',
  PROCESSING: 'info',
  ACCEPTED: 'success',
  REJECTED: 'error',
  ERROR: 'error',
  CANCELLED: 'default',
}

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {children}
      </Box>
      <Divider sx={{ mt: 2 }} />
    </Box>
  )
}

function DetailFieldFull({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box sx={{ mb: 1, gridColumn: '1 / -1' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function RequestDetailDialog({ req, onClose, onCancel }: { req: TitleOrderRequest; onClose: () => void; onCancel: () => void;}) {
  const cancelMutation = useCancelOrderRequest(req.RequestId)
  const { data } = useGetConfig();
  const sendToOptions = data?.org?.OrderRequestFeature?.SendToOptions ?? []

  async function handleCancel() {
    await cancelMutation.mutateAsync(undefined)
    onCancel()
  }
  const rawLoanAmount = req.LenderInfo?.LoanAmount
  const loanAmountNum = rawLoanAmount ? Number(String(rawLoanAmount).replace(/[^\d.]/g, '')) : NaN
  const loanAmount = rawLoanAmount
    ? Number.isFinite(loanAmountNum) ? `$${loanAmountNum.toLocaleString()}` : String(rawLoanAmount)
    : undefined

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6">Order Request Details</Typography>
          <Typography variant="caption" color="text.secondary">ID: {req.RequestId}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={req.Status} color={STATUS_COLORS[req.Status] ?? 'default'} size="small" />
          <IconButton onClick={onClose} size="small"><CloseRounded /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>

        <DetailSection title="Transaction Information">
          <DetailField label="Order Type" value={req.OrderType} />
          <DetailField label="Closing Date" value={req.ClosingDate} />
          <DetailField label="Submitted" value={new Date(req.CreatedAt).toLocaleString()} />
          <DetailField label="Route To" value={sendToOptions.find((o) => o.id === req.RouteToId)?.name ?? req.RouteToId} />
        </DetailSection>

        {req.RequestedBy && (
          <DetailSection title="Requested By">
            <DetailField label="Role" value={req.RequestedBy.Role} />
            <DetailField label="Name" value={`${req.RequestedBy.FirstName} ${req.RequestedBy.LastName}`} />
            <DetailField label="Email" value={req.RequestedBy.Email} />
            <DetailField label="Phone" value={req.RequestedBy.Phone} />
            <DetailField label="Fax" value={req.RequestedBy.Fax} />
            <DetailField label="Company" value={req.RequestedBy.CompanyName} />
            <DetailFieldFull
              label="Company Address"
              value={[req.RequestedBy.CompanyAddress1, req.RequestedBy.CompanyAddress2, req.RequestedBy.CompanyCity, req.RequestedBy.CompanyState, req.RequestedBy.CompanyZip].filter(Boolean).join(', ') || null}
            />
          </DetailSection>
        )}

        {req.PropertyInfo && (
          <DetailSection title="Property Information">
            <DetailFieldFull label="Address" value={[req.PropertyInfo.Address1, req.PropertyInfo.Address2].filter(Boolean).join(' ')} />
            <DetailField label="City" value={req.PropertyInfo.City} />
            <DetailField label="State / Zip" value={`${req.PropertyInfo.State} ${req.PropertyInfo.Zip}`} />
            <DetailField label="Tax ID" value={req.PropertyInfo.TaxId} />
            <DetailField label="Vacant Land" value={req.PropertyInfo.VacantLand !== undefined ? (req.PropertyInfo.VacantLand ? 'Yes' : 'No') : null} />
            <DetailFieldFull label="Legal Description" value={req.PropertyInfo.LegalDescription} />
          </DetailSection>
        )}

        {req.Borrowers?.length ? (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              {`Borrower${req.Borrowers.length > 1 ? 's' : ''}`}
            </Typography>
            {req.Borrowers.map((b, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                {req.Borrowers!.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Borrower {i + 1}</Typography>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                  <DetailField label="Name" value={[b.FirstName, b.MiddleName, b.LastName].filter(Boolean).join(' ')} />
                  <DetailField label="Type" value={b.Type.charAt(0).toUpperCase() + b.Type.slice(1)} />
                  <DetailField label="Email" value={b.Email} />
                  <DetailField label="Phone" value={b.Phone} />
                  {b.ContactAddress && (
                    <DetailFieldFull label="Contact Address" value={[b.ContactAddress.Address1, b.ContactAddress.Address2, b.ContactAddress.City, b.ContactAddress.State, b.ContactAddress.Zip].filter(Boolean).join(', ')} />
                  )}
                </Box>
                {i < req.Borrowers!.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
            <Divider />
          </Box>
        ) : null}

        {req.LenderInfo && Object.values(req.LenderInfo).some(Boolean) && (
          <DetailSection title="Lender Information">
            <DetailField label="Lender Name" value={req.LenderInfo.LenderName} />
            <DetailField label="Financing Type" value={req.LenderInfo.FinancingType} />
            <DetailField label="Loan Amount" value={loanAmount} />
            <DetailField label="Loan Number" value={req.LenderInfo.LoanNumber} />
            <DetailField label="Lender Contact" value={[req.LenderInfo.LenderFirstName, req.LenderInfo.LenderLastName].filter(Boolean).join(' ') || null} />
            <DetailField label="Lender Email" value={req.LenderInfo.LenderEmail} />
            <DetailField label="Lender Phone" value={req.LenderInfo.LenderPhone} />
          </DetailSection>
        )}

        {req.Comments && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Comments</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{req.Comments}</Typography>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {req.Documents.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Documents ({req.Documents.length})</Typography>
            {req.Documents.map((doc, i) => (
              <Typography key={i} variant="body2" sx={{ py: 0.5, borderBottom: i < req.Documents.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                {doc.OriginalFileName}
              </Typography>
            ))}
          </Box>
        )}

      </DialogContent>
      <DialogActions>
        {req.Status === 'PENDING' && (
          <Button color="error" onClick={handleCancel} disabled={cancelMutation.isLoading}>
            {cancelMutation.isLoading ? 'Cancelling...' : 'Cancel Request'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

type OrderRequestRowProps = {
  req: TitleOrderRequest
  setSelectedRequest: React.Dispatch<React.SetStateAction<TitleOrderRequest | null>>
} & React.ComponentPropsWithoutRef<typeof TableRow>

const OrderRequestRow = forwardRef<HTMLTableRowElement, OrderRequestRowProps>(({ req, setSelectedRequest, ...tableRowProps }, ref) => {
  const statusColor = STATUS_COLORS[req.Status] ?? 'default'

  return (
    <TableRow
      ref={ref}
      key={req.RequestId}
      hover
      onClick={() => setSelectedRequest(req)}
      sx={{ cursor: 'pointer' }}
      {...tableRowProps}
    >
      <TableCell>{new Date(req.CreatedAt).toLocaleDateString()}</TableCell>
      <TableCell>{req.OrderType ?? '—'}</TableCell>
      <TableCell>
        {req.PropertyInfo
          ? <><Typography variant="body2">{req.PropertyInfo.Address1}</Typography><Typography variant="caption" color="text.secondary">{req.PropertyInfo.City}, {req.PropertyInfo.State} {req.PropertyInfo.Zip}</Typography></>
          : '—'}
      </TableCell>
      <TableCell>
        {req.Borrowers?.length
          ? req.Borrowers.map((b, i) => <Typography key={i} variant="body2">{b.FirstName} {b.LastName}</Typography>)
          : '—'}
      </TableCell>
      <TableCell>
        <Chip label={req.Status} color={statusColor} size="small" />
      </TableCell>
    </TableRow>
  )
})

OrderRequestRow.displayName = 'OrderRequestRow'

export function OrderRequestPage() {
  const [showForm, setShowForm] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [lastSubmittedId, setLastSubmittedId] = useState<string | null>(null)
  const [selectedRequest, setSelectedRequest] = useState<TitleOrderRequest | null>(null)

  const { data, isLoading, refetch } = useGetOrderRequests()
  const orderRequests = data?.OrderRequests ?? []

  function handleSuccess(requestId: string) {
    setLastSubmittedId(requestId)
    setShowForm(false)
    refetch()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto', mt: '70px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>
          Title Order Requests
        </Typography>
        {!showForm && (
          <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => { setShowForm(true); setFormKey((k) => k + 1); setLastSubmittedId(null) }}>
            New Request
          </Button>
        )}
      </Box>

      {lastSubmittedId && !showForm && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your title order request has been submitted successfully. A team member will follow up with you shortly.
        </Alert>
      )}

      {showForm ? (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>New Title Order Request</Typography>
          <Divider sx={{ mb: 2 }} />
          <OrderRequestForm key={formKey} onSuccess={handleSuccess} />
          <Button sx={{ mt: 1 }} onClick={() => setShowForm(false)}>Cancel</Button>
        </>
      ) : (
        <>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : orderRequests.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No order requests yet.
              </Typography>
              <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => { setShowForm(true); setFormKey((k) => k + 1) }}>
                Submit Your First Request
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={8} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Order Type</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Borrower(s)</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderRequests.map((req) => (
                    req.Status === 'ERROR' ? (
                      <Tooltip key={req.RequestId} title={'There was an error processing this request. Please contact support.'} arrow>
                        <OrderRequestRow req={req} setSelectedRequest={setSelectedRequest} />
                      </Tooltip>
                    ) : (
                      <OrderRequestRow key={req.RequestId} req={req} setSelectedRequest={setSelectedRequest} />
                    )
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {selectedRequest && (
        <RequestDetailDialog
          req={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onCancel={() => { setSelectedRequest(null); refetch() }}
        />
      )}
    </Box>
  )
}
